import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Field, StatusPill, Textarea, type StatusTone } from '@bb/ui';
import { ApiError, type AdminApi, type CampaignView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';

const STATUS: Record<CampaignView['approval']['status'], [string, StatusTone]> = {
  not_required: ['Approval not required', 'neutral'],
  missing: ['Approval needed', 'warning'],
  pending: ['Waiting for review', 'info'],
  approved: ['Approved', 'success'],
  rejected: ['Rejected', 'danger'],
};
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** What changed against the running version (the revision the reviewer approves). */
function RevisionDiff({ environmentId, campaign }: { environmentId: string; campaign: CampaignView }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const from = campaign.published?.revision;
  const to = campaign.draft?.revision;
  const diff = useQuery({ queryKey: ['campaigns', environmentId, 'diff', campaign.id, from, to], queryFn: () => api.campaignDiff(environmentId, campaign.id, from!, to!), enabled: Boolean(from && to) });
  if (!from || !to) return <p className="hint">First version: everything in the draft is new.</p>;
  if (!diff.data) return null;
  return (
    <table className="table" aria-label="Changes against the running version">
      <thead>
        <tr>
          <th scope="col">Field</th>
          <th scope="col">Running (revision {from})</th>
          <th scope="col">Draft (revision {to})</th>
        </tr>
      </thead>
      <tbody>
        {diff.data.changes.map((c) => (
          <tr key={c.path}>
            <td>
              <code>{c.path}</code>
            </td>
            <td>
              <code>{c.before === undefined ? '—' : JSON.stringify(c.before)}</code>
            </td>
            <td>
              <code>{c.after === undefined ? '—' : JSON.stringify(c.after)}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Review of the draft (BB-106): its approval status, the messaging rules it triggers, the changes against the running
 * version, and the actions — ask for approval (editors), approve or reject (reviewers).
 */
export function ApprovalPanel({ environmentId, campaign }: { environmentId: string; campaign: CampaignView }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [comment, setComment] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['campaigns', environmentId] });
  const ask = useMutation({ mutationFn: () => api.requestApproval(environmentId, campaign.id, comment.trim() || undefined), onSuccess: () => (setComment(''), refresh()) });
  const decide = useMutation({ mutationFn: (decision: 'approve' | 'reject') => api.decideApproval(environmentId, campaign.approval.request!.id, decision, comment.trim() || undefined), onSuccess: () => (setComment(''), refresh()) });
  const { approval } = campaign;
  if (!campaign.draft && !approval.required) return null;
  const [label, tone] = STATUS[approval.status];
  const request = approval.request;
  const canAsk = permissions.has('campaigns.edit') && campaign.draft && (approval.status === 'missing' || approval.status === 'rejected');
  const canDecide = permissions.has('messages.approve') && approval.status === 'pending';
  return (
    <section aria-label="Review" className="panel">
      <h2>
        Review <StatusPill tone={tone}>{label}</StatusPill>
      </h2>
      {request && (
        <p className="hint">
          Revision {request.revision}, asked by {request.requestedBy?.email ?? 'someone'} {formatWhen(request.requestedAt)}
          {request.requestComment ? ` — “${request.requestComment}”` : ''}
          {request.decidedBy ? `; ${request.status} by ${request.decidedBy.email ?? 'someone'}${request.decisionComment ? ` — “${request.decisionComment}”` : ''}` : ''}
        </p>
      )}
      {request && request.triggeredRules.length > 0 && (
        <ul aria-label="Messaging rules">
          {request.triggeredRules.map((r) => (
            <li key={r.ruleId}>
              {r.reason}
              {r.requiresRole ? ` — needs an approver with the role ${r.requiresRole}` : ''}
            </li>
          ))}
        </ul>
      )}
      {campaign.draft && <RevisionDiff environmentId={environmentId} campaign={campaign} />}
      {(canAsk || canDecide) && (
        <Field label="Comment">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
      )}
      <div className="toolbar">
        {canAsk && (
          <Button variant="primary" loading={ask.isPending} onClick={() => ask.mutate()}>
            Request approval
          </Button>
        )}
        {canDecide && (
          <>
            <Button variant="primary" loading={decide.isPending} onClick={() => decide.mutate('approve')}>
              Approve
            </Button>
            <Button variant="danger" disabled={comment.trim().length < 3 || decide.isPending} onClick={() => decide.mutate('reject')}>
              Reject
            </Button>
          </>
        )}
      </div>
      {(ask.isError || decide.isError) && <p role="alert">{errorText(ask.error ?? decide.error)}</p>}
    </section>
  );
}
